import { create } from 'zustand';

export type NotificationType = 'danger' | 'warning';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: Date;
  isRead: boolean;
  actionUrl?: string;
}

export interface NotificationStore {
  isNotificationOpen: boolean;
  notifications: Notification[];
  
  // Actions
  toggleNotification: () => void;
  openNotification: () => void;
  closeNotification: () => void;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  removeNotification: (notificationId: string) => void;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void;
}

// 샘플 알림 데이터
const sampleNotifications: Notification[] = [
  {
    id: 'notif-1',
    type: 'danger',
    title: '작업 지연 위험',
    message: 'BTS - Dynamite 리믹스 작업이 예정일을 2일 초과했습니다. 즉시 확인이 필요합니다.',
    timestamp: new Date(Date.now() - 1000 * 60 * 30), // 30분 전
    isRead: false,
    actionUrl: '/orders/ORD-001'
  },
  {
    id: 'notif-2',
    type: 'danger',
    title: '긴급 작업 지연',
    message: 'LE SSERAFIM - UNFORGIVEN 번역 작업이 마감일을 1일 초과했습니다.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2시간 전
    isRead: false,
    actionUrl: '/orders/ORD-002'
  },
  {
    id: 'notif-3',
    type: 'warning',
    title: '등록 없는 의뢰 발견',
    message: 'IVE - XOXZ 프로젝트가 3주째 등록되지 않고 있습니다. 검토가 필요합니다.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 4), // 4시간 전
    isRead: false,
    actionUrl: '/orders/ORD-003'
  },
  {
    id: 'notif-4',
    type: 'warning',
    title: '장기 미등록 의뢰',
    message: 'NewJeans - Get Up MIDI 리메이크 의뢰가 2주째 대기 중입니다.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 8), // 8시간 전
    isRead: true,
    actionUrl: '/orders/ORD-004'
  },
  {
    id: 'notif-5',
    type: 'danger',
    title: '작업자 연락 두절',
    message: '(G)I-DLE - Queencard 작업 담당자와 24시간째 연락이 되지 않습니다.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 12), // 12시간 전
    isRead: true,
    actionUrl: '/workers/WRK-001'
  },
  {
    id: 'notif-6',
    type: 'warning',
    title: '예산 초과 위험',
    message: 'aespa - Spicy 프로젝트의 작업 비용이 예산의 85%에 도달했습니다.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24), // 1일 전
    isRead: true,
    actionUrl: '/projects/PRJ-001'
  }
];

export const useNotificationStore = create<NotificationStore>((set) => ({
  isNotificationOpen: false,
  notifications: sampleNotifications,

  toggleNotification: () => set((state) => ({ 
    isNotificationOpen: !state.isNotificationOpen 
  })),
  
  openNotification: () => set({ isNotificationOpen: true }),
  
  closeNotification: () => set({ isNotificationOpen: false }),
  
  markAsRead: (notificationId) => set((state) => ({
    notifications: state.notifications.map(notification =>
      notification.id === notificationId
        ? { ...notification, isRead: true }
        : notification
    )
  })),
  
  markAllAsRead: () => set((state) => ({
    notifications: state.notifications.map(notification => ({
      ...notification,
      isRead: true
    }))
  })),
  
  removeNotification: (notificationId) => set((state) => ({
    notifications: state.notifications.filter(notification => 
      notification.id !== notificationId
    )
  })),
  
  addNotification: (notification) => set((state) => ({
    notifications: [
      {
        ...notification,
        id: `notif-${Date.now()}`,
        timestamp: new Date()
      },
      ...state.notifications
    ]
  }))
}));
